// @flow
import * as React from 'react';
import { localize } from '@ta/common.i18n';
import classnames from '@ta/common.classnames';
import { escapeHtml } from '@ta/common.text-html';
import {
  useInnerHTMLEventListeners,
  useMentionsInteractionContext,
} from '@ta/social.mentions-tracking';
import styles from './ProfileLabel.less';

type Props = {|
  name: string,
  isVerified?: boolean,
  url?: string,
  lowercasePrefix?: boolean,
  additionalLabelStyle?: boolean,
|};

const getProfileLabelText = (
  name: string,
  isVerified: boolean,
  url?: string,
  lowercasePrefix: boolean,
) => {
  if (url) {
    if (lowercasePrefix) {
      return localize('mentions_profile_label_a_lower', {
        name: escapeHtml(name),
        url,
        prefixClassName: styles.prefix,
        nameClassName: classnames({ [styles.verified]: isVerified }, styles.actor),
      }); // by <a>{name}</a>
    }
    return localize('mentions_profile_label_a', {
      name: escapeHtml(name),
      url,
      prefixClassName: styles.prefix,
      nameClassName: classnames({ [styles.verified]: isVerified }, styles.actor),
    }); // By <a>{name}</a>
  }
  if (lowercasePrefix) {
    return localize('mentions_profile_label_span_lower', {
      name: escapeHtml(name),
      prefixClassName: styles.prefix,
      nameClassName: classnames({ [styles.verified]: isVerified }, styles.actor),
    }); // by <span>{name}</span>
  }
  return localize('mentions_profile_label_span', {
    name: escapeHtml(name),
    prefixClassName: styles.prefix,
    nameClassName: classnames({ [styles.verified]: isVerified }, styles.actor),
  }); // By <span>{name}</span>
};

const ProfileLabel = ({
  name,
  isVerified = false,
  url,
  lowercasePrefix = false,
  additionalLabelStyle = false,
}: Props) => {
  const { userClick } = useMentionsInteractionContext();
  const onClick = React.useCallback(
    event => {
      // Only track interactions on a tag clicks
      if (((event.target: any): HTMLElement).tagName === 'A' && userClick) {
        userClick(event);
      }
    },
    [userClick],
  );
  const labelRef = useInnerHTMLEventListeners({ onClick }, url);
  const label = getProfileLabelText(name, isVerified, url, lowercasePrefix);
  return (
    <span
      className={classnames(styles.label, { [styles.additionalLabelStyle]: additionalLabelStyle })}
      ref={labelRef}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: label }}
      key={url}
    />
  );
};

/** The ProfileLabel component */
export default React.memo<Props>(ProfileLabel);
